import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Spinner } from '../Spinner';

/**
 * Control scale, measured off the approved spike rather than eyeballed.
 *
 * The spike draws two button heights: 26px for chrome — page-header actions,
 * table pagination, filter apply/clear, 41 of its 50 buttons — and 31px for the
 * full-width primary submit at the foot of an auth form. So `sm` is 26 and `md`
 * is 32 (the nearest Tailwind step to 31), and chrome should ask for `sm`.
 *
 * This comment previously claimed 32px "follows the approved spike". It did
 * not. The buttons were 40px, the review note said they were too big, and the
 * number they were changed to was assumed instead of measured — which is how a
 * fix lands 6px out and reads as done. Anything asserted here should be
 * re-measurable from docs/design/design-spike-3.html.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[7px] text-[13px] font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:select-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline: 'border border-input bg-card hover:bg-muted',
        ghost: 'hover:bg-muted',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-[26px] px-2.5 text-xs',
        md: 'h-8 px-3',
        lg: 'h-10 px-4 text-sm',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Shows a spinner over the label while preserving the button's width. */
  loading?: boolean;
  /** Grow to the full width of the parent. */
  fullWidth?: boolean;
  /** Icon rendered beside the label. */
  icon?: React.ReactNode;
  /** @default 'left' */
  iconPosition?: 'left' | 'right';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      type = 'button',
      loading = false,
      fullWidth = false,
      icon,
      iconPosition = 'left',
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), fullWidth && 'w-full', className)}
      {...props}
    >
      {loading || icon ? (
        <span className="relative inline-flex">
          <span
            className={cn(
              'flex flex-row items-center gap-2',
              iconPosition === 'right' && 'flex-row-reverse',
              loading && 'invisible',
            )}
          >
            {icon && (
              <span className="h-4 w-4 [&>svg]:h-full [&>svg]:w-full" aria-hidden>
                {icon}
              </span>
            )}
            {children != null && <span>{children}</span>}
          </span>
          {loading && <Spinner centerOverContent />}
        </span>
      ) : (
        children
      )}
    </button>
  ),
);
Button.displayName = 'Button';

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonIcon = React.ReactNode;
export type ButtonIconPosition = 'left' | 'right';
